import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Mail, Users, Share2, ChevronDown } from "lucide-react";
import { NewsletterEditor } from "./NewsletterEditor";
import AdminListPage from "./AdminListPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_FIELDS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "youtube", label: "YouTube" },
];

const SocialConfig = () => {
  const [open, setOpen] = useState(false);
  const [social, setSocial] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/newsletter-config`, { withCredentials: true })
      .then((r) => setSocial(r.data.social || {})).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await axios.patch(`${API}/admin/newsletter-config`, social, { withCredentials: true });
      toast.success("Redes sociais guardadas.");
      setOpen(false);
    } catch { toast.error("Erro a guardar."); }
  };

  return (
    <div className="bg-white border border-[hsl(var(--border))]" data-testid="nl-social-config">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-sm" data-testid="nl-social-toggle">
        <span className="inline-flex items-center gap-2"><Share2 className="h-4 w-4 text-[hsl(var(--accent))]" /> Redes sociais do rodapé</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-[hsl(var(--border))] pt-3">
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Só aparecem no rodapé da newsletter os links preenchidos.</p>
          {SOCIAL_FIELDS.map((f) => (
            <div key={f.key} className="grid grid-cols-[90px_1fr] items-center gap-2">
              <span className="text-xs">{f.label}</span>
              <input value={social[f.key] || ""} onChange={(e) => setSocial({ ...social, [f.key]: e.target.value })}
                placeholder={`https://${f.key}.com/…`}
                className="bg-white border border-[hsl(var(--border))] px-3 py-1.5 text-xs"
                data-testid={`nl-social-${f.key}`} />
            </div>
          ))}
          <div className="flex justify-end">
            <button onClick={save} className="px-4 py-2 bg-[hsl(var(--primary))] text-white text-[10px] uppercase tracking-[0.18em] hover:bg-[#093630]" data-testid="nl-social-save">Guardar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminNewsletter() {
  const [tab, setTab] = useState("editions");
  const [items, setItems] = useState([]);
  const [subsCount, setSubsCount] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [rn, rc] = await Promise.all([
        axios.get(`${API}/admin/newsletters`, { withCredentials: true }),
        axios.get(`${API}/admin/newsletters-recipients`, { withCredentials: true }),
      ]);
      setItems(rn.data.items || []);
      setSubsCount(rc.data.count);
    } catch { toast.error("Erro a carregar."); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (!editingId) load(); }, [editingId]);

  const create = async () => {
    try {
      const r = await axios.post(`${API}/admin/newsletters`, { subject: "", blocks: [] }, { withCredentials: true });
      setEditingId(r.data.id);
    } catch { toast.error("Erro a criar."); }
  };

  const duplicate = async (n) => {
    try {
      const r = await axios.post(`${API}/admin/newsletters/${n.id}/duplicate`, {}, { withCredentials: true });
      setEditingId(r.data.id);
    } catch { toast.error("Erro a duplicar."); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Apagar "${n.subject || "(sem assunto)"}"?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletters/${n.id}`, { withCredentials: true });
      load();
    } catch { toast.error("Erro a apagar."); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-newsletter-page">
      <div className="overline">Newsletter</div>
      <h1 className="display-heading text-3xl mt-1">Newsletter CIEES.</h1>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
        Crie edições da newsletter com blocos de texto, imagens, eventos e citações, pré-visualize e envie
        a todos os subscritores{subsCount != null ? ` (${subsCount} ativos)` : ""}.
      </p>

      <div className="mt-8 flex gap-1 border-b border-[hsl(var(--border))]">
        {[["editions", "Edições"], ["subscribers", "Subscritores"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-5 py-3 text-sm border-b-2 -mb-px transition-colors ${
              tab === k ? "border-[hsl(var(--accent))] text-[hsl(var(--primary))]"
                : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
            data-testid={`nl-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "subscribers" ? (
        <AdminListPage overline="Newsletter" title="Subscritores"
          type="newsletter" endpoint="/admin/newsletter" csvUrl="/admin/newsletter.csv"
          archiveBase="/admin/newsletter" defaultEmpty="Ainda sem subscritores." embedded />
      ) : editingId ? (
        <NewsletterEditor id={editingId} onBack={() => setEditingId(null)} />
      ) : (
        <div className="mt-6 max-w-3xl space-y-5">
          <div className="flex items-center justify-between gap-4">
            <button onClick={create} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630]" data-testid="nl-create">
              <Plus className="h-4 w-4" /> Nova newsletter
            </button>
            {subsCount != null && (
              <span className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                <Users className="h-3.5 w-3.5" /> {subsCount} subscritor(es) ativos
              </span>
            )}
          </div>

          <SocialConfig />

          {loading ? (
            <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p>
          ) : !items.length ? (
            <div className="bg-white border border-[hsl(var(--border))] p-12 text-center">
              <Mail className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
              <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Ainda sem newsletters. Crie a primeira edição.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="nl-list">
              {items.map((n) => (
                <div key={n.id} className="bg-white border border-[hsl(var(--border))] p-4 flex items-start justify-between gap-3" data-testid={`nl-row-${n.id}`}>
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setEditingId(n.id)}>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium truncate">{n.subject || "(sem assunto)"}</h3>
                      {n.status === "sent" ? (
                        <span className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-900 border border-emerald-200 px-1.5 py-0.5">Enviada</span>
                      ) : n.status === "scheduled" ? (
                        <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-900 border border-amber-200 px-1.5 py-0.5">Agendada</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider bg-[hsl(var(--muted))] px-1.5 py-0.5">Rascunho</span>
                      )}
                    </div>
                    <div className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">
                      {n.block_count} bloco(s)
                      {n.status === "sent"
                        ? ` · enviada a ${n.emails_sent}/${n.recipient_count} em ${new Date(n.sent_at).toLocaleString("pt-PT")}`
                        : n.status === "scheduled"
                        ? ` · envio agendado para ${(n.scheduled_at_local || "").replace("T", " às ")}`
                        : ` · atualizada em ${new Date(n.updated_at).toLocaleString("pt-PT")}`}
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 shrink-0">
                    <button onClick={() => duplicate(n)} title="Duplicar" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`nl-duplicate-${n.id}`}>
                      <Copy className="h-4 w-4" />
                    </button>
                    <button onClick={() => setEditingId(n.id)} title="Editar" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`nl-edit-${n.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(n)} title="Apagar" className="text-[hsl(var(--muted-foreground))] hover:text-red-700" data-testid={`nl-delete-${n.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
